import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  Flex,
  Stack,
  Text,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { giftWallet } from "../../../../../store/gifts/newWalletSlice";
import { getAllUsers } from "../../../../../store/allusers/userAllSlice";
import { FaSearch, FaChevronDown } from "react-icons/fa";

const GiftWalletModal = ({ onClose, onDone }) => {
  const dispatch = useDispatch();
  const wallet = useSelector((s) => s.wallet);
  const usersAll = useSelector((s) => s.usersAll);

  const [target, setTarget] = useState("owner"); // owner | user
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef();

  useEffect(() => {
    dispatch(getAllUsers({ page: 1, size: 200, type: target }));
  }, [dispatch, target]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  // Filter users based on search query
  const filteredUsers = usersAll.data.filter((user) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setValue("userId", user._id);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleTargetChange = (newTarget) => {
    setTarget(newTarget);
    setSelectedUser(null);
    setValue("userId", "");
    setSearchQuery("");
  };

  return (
    <Modal isOpen onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form
          onSubmit={handleSubmit(async (v) => {
            await dispatch(
              giftWallet({ owner: v.userId, wallet: Number(v.wallet) })
            ).unwrap();
            onDone();
          })}
        >
          <ModalHeader>Gift Wallet</ModalHeader>
          <ModalBody>
            {wallet.error && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {wallet.error}
              </Alert>
            )}

            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Target Type</FormLabel>
                <Select
                  bg="white"
                  value={target}
                  onChange={(e) => handleTargetChange(e.target.value)}
                >
                  <option value="owner">Owner</option>
                  {/* <option value="user">User</option> */}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Target ({target})</FormLabel>
                
                <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
                  <MenuButton
                    as={Button}
                    rightIcon={<FaChevronDown />}
                    variant="outline"
                    w="100%"
                    justifyContent="space-between"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ borderColor: "gray.300" }}
                    _expanded={{ borderColor: "blue.500" }}
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    {selectedUser ? selectedUser.name : `Select a ${target}`}
                  </MenuButton>
                  
                  <MenuList maxH="300px" overflowY="auto" p={0}>
                    {/* Search Input inside dropdown */}
                    <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                      <Flex>
                        <Input
                          ref={searchInputRef}
                          placeholder={`Search ${target}s...`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          size="sm"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          _focus={{
                            borderColor: "blue.500",
                            boxShadow: "0 0 0 1px blue.500",
                          }}
                        />
                        <Box ml={2} color="gray.400" alignSelf="center">
                          <FaSearch size={14} />
                        </Box>
                      </Flex>
                    </Box>

                    {/* Results count */}
                    {searchQuery && (
                      <Box px={3} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                        <Text fontSize="xs" color="gray.600">
                          {filteredUsers.length} {filteredUsers.length === 1 ? target : target + 's'} found
                        </Text>
                      </Box>
                    )}

                    {/* User list */}
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <MenuItem
                          key={user._id}
                          onClick={() => handleUserSelect(user)}
                          _hover={{ bg: "blue.50" }}
                          py={2}
                        >
                          {user.name}
                        </MenuItem>
                      ))
                    ) : (
                      <Box p={3} textAlign="center">
                        <Text fontSize="sm" color="gray.500">
                          {searchQuery ? `No ${target}s found matching "${searchQuery}"` : `No ${target}s available`}
                        </Text>
                      </Box>
                    )}
                  </MenuList>
                </Menu>

                {/* Hidden input for form validation */}
                <Input
                  {...register("userId", { required: "required" })}
                  type="hidden"
                />
                
                {errors.userId && (
                  <Text color="red.500" mt={1}>
                    {errors.userId.message}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Amount</FormLabel>
                <Input
                  type="number"
                  step="1"
                  {...register("wallet", {
                    required: "required",
                    valueAsNumber: true,
                  })}
                />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Flex gap={3}>
              <Button
                type="submit"
                colorScheme="green"
                isLoading={wallet.isLoading}
              >
                Save
              </Button>
              <Button onClick={onClose}>Close</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default GiftWalletModal;
